import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setExperienceLive, setSubmitting, setDownloadFailed } from '../../store/slices/exhibitSlice';
import TcButton from '../TcButton';
import TcPaper from '../TcPaper';
import TcImage from '../TcImage';

export default function TimeCapsule() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { name, selfieUrl } = useAppSelector(state => state.user);
    const { isSubmitting, downloadFailed, experienceLive } = useAppSelector(state => state.exhibit);
    
    const [step, setStep] = useState<'intro' | 'submission' | 'experience-live' | 'download-failed'>('intro');
    
    const handleSubmit = () => {
        dispatch(setSubmitting(true));
        setStep('submission');
        
        // Simulate API call to submit visitor data
        setTimeout(() => {
            dispatch(setSubmitting(false));
            dispatch(setExperienceLive(true));
            setStep('experience-live');
        }, 3000);
    };
    
    const handleDownload = () => {
        // Simulate download - random success/failure
        const isSuccess = Math.random() > 0.3;
        
        if (isSuccess) {
            // In a real app, this would trigger a download
            console.log('Download successful');
            alert('Downloaded successfully!');
        } else {
            dispatch(setDownloadFailed(true));
            setStep('download-failed');
        }
    };
    
    const handleRetryDownload = () => {
        dispatch(setDownloadFailed(false));
        setStep('experience-live');
    };
    
    // Introduction screen - Wall intro
    if (step === 'intro') {
        return (
            <div className="main-content-wrapper bg-black">
                <TcPaper classNameStyle="mb-6">
                    <h1>{t('exhibits.timeCapsule.title')}</h1>
                </TcPaper>

                <TcPaper classNameStyle="mb-6">
                    <div className="general-content">
                        <p dangerouslySetInnerHTML={{ __html: t('common.comingSoon') }} />
                    </div>
                </TcPaper>
                
                {/* <TcImage classNameStyle="mb-8">
                    <img 
                        src="/images/time-capsule-intro.jpg" 
                        alt={t('exhibits.timeCapsule.title')} 
                        className="w-full rounded-lg"
                    />
                </TcImage>
                
                <TcPaper classNameStyle="general-content px-6">
                    <p className="mb-6" 
                        dangerouslySetInnerHTML={{ __html: t('exhibits.timeCapsule.description') }}
                    />
                </TcPaper>
                
                <div className="button-wrapper px-6">
                    <TcButton 
                        handleClick={handleSubmit}
                        primary
                    >
                        {t('exhibits.timeCapsule.createMemory')}
                    </TcButton>
                </div> */}
            </div>
        );
    }
    
    // Submission screen - when user is submitting data
    if (step === 'submission') {
        return (
            <div className="main-content-wrapper bg-black">
                <TcPaper classNameStyle="mb-6">
                    <h1>{t('exhibits.timeCapsule.selectExperiences')}</h1>
                </TcPaper>
                
                <div className="mb-6">
                    <img 
                        src={selfieUrl || "/images/default-avatar.png"} 
                        alt="Your Selfie" 
                        className="w-48 h-48 object-cover mx-auto rounded-full border-4 border-blue-500"
                    />
                </div>
                
                <p className="text-xl mb-8">
                    {t('exhibits.bookOfMyths.hello', { name: name || t('common.welcome') })}
                </p>
                
                <p className="mb-6">
                    {t('exhibits.timeCapsule.journeyPreserved')}
                </p>
                
                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mb-6">
                    <div className="h-full bg-blue-500 rounded-full animate-pulse" style={{ width: '80%' }}></div>
                </div>
                
                <p className="text-sm text-gray-500">{t('exhibits.bookOfMyths.processing')}</p>
            </div>
        );
    }
    
    // Experience live screen - when submission is complete
    if (step === 'experience-live') {
        return (
            <div className="main-content-wrapper bg-black">
                <TcPaper classNameStyle="mb-6">
                    <h1>{t('exhibits.timeCapsule.capsuleComplete')}</h1>
                </TcPaper>
                
                <div className="mb-6 relative">
                    <img 
                        src="/images/time-capsule-wall.jpg" 
                        alt="Wall Display" 
                        className="w-full rounded-lg"
                    />
                    <div className="absolute bottom-4 right-4 w-24 h-24 rounded-full overflow-hidden border-2 border-white">
                        <img 
                            src={selfieUrl || "/images/default-avatar.png"} 
                            alt="Your Selfie" 
                            className="w-full h-full object-cover"
                        />
                    </div>
                </div>
                
                <p className="text-xl mb-6">
                    {t('exhibits.bookOfMyths.storyLive')}
                </p>
                
                <p className="mb-8">
                    {t('exhibits.timeCapsule.displayMemory')}
                </p>
                
                <div className="flex flex-col space-y-4 w-full max-w-xs mx-auto">
                    <TcButton 
                        handleClick={handleDownload}
                        primary
                    >
                        {t('exhibits.timeCapsule.downloadImage')}
                    </TcButton>
                    
                    <TcButton 
                        handleClick={() => navigate('/')}
                    >
                        {t('common.exploreMore')}
                    </TcButton>
                </div>
            </div>
        );
    }
    
    // Download failed screen
    if (step === 'download-failed') {
        return (
            <div className="main-content-wrapper bg-black">
                <TcPaper classNameStyle="mb-6">
                    <h1>{t('exhibits.timeCapsule.downloadFailed.title')}</h1>
                </TcPaper>
                
                <div className="mb-8">
                    <img 
                        src="/images/download-failed.png" 
                        alt={t('exhibits.timeCapsule.downloadFailed.title')} 
                        className="w-48 h-48 mx-auto"
                    />
                </div>
                
                <p className="mb-8">
                    {t('exhibits.timeCapsule.downloadFailed.message')}
                </p>
                
                <div className="flex flex-col space-y-4 w-full max-w-xs mx-auto">
                    <TcButton 
                        handleClick={handleRetryDownload}
                        primary
                    >
                        {t('common.tryAgain')}
                    </TcButton>
                    
                    <TcButton 
                        handleClick={() => navigate('/')}
                    >
                        {t('exhibits.timeCapsule.downloadFailed.skipButton')}
                    </TcButton>
                </div>
            </div>
        );
    }
    
    // Default return - should never reach here
    return null;
} 